import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import ConsentBar from "../components/ConsentBar";
import CountUp from "react-countup";
import ContactForm from "../components/ContactForm";


// Animation Variant
const fadeInUp = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0 },
};

// FAQ Data
const faqData = [
  {
    q: "What is UPSC Journey?",
    a: "UPSC Journey is a complete UPSC preparation platform that combines editorials, notes, MCQs, answer writing, test series, and structured study systems in one place."
  },

  {
    q: "How is UPSC Journey different from other platforms?",
    a: "Instead of giving random content from multiple sources, UPSC Journey follows a structured preparation workflow that connects learning, practice, revision, and testing together."
  },

  {
    q: "Can beginners start preparation with UPSC Journey?",
    a: "Yes. The platform is designed for both beginners and experienced aspirants, including college students, working professionals, and repeat aspirants."
  },

  {
    q: "Do I need multiple resources along with UPSC Journey?",
    a: "No. UPSC Journey is designed to reduce resource overload by providing structured preparation tools, notes, practice, and revision systems in one platform."
  },

  {
    q: "Is UPSC Journey free to use?",
    a: "Yes. Users can create a free account and explore selected features before upgrading to Premium access."
  },

  {
    q: "What can I access after free signup?",
    a: "Free users can explore the platform interface, access selected content, and understand how the preparation workflow works before upgrading."
  },

  {
    q: "What is included in Premium access?",
    a: "Premium access unlocks the complete preparation ecosystem including full notes library, daily MCQs, editorials archive, answer writing practice, test series, and advanced preparation tools."
  },

  {
    q: "Are study plans included in Premium?",
    a: "Study plans are available separately as an optional add-on designed for aspirants who want extra structure and daily preparation guidance."
  },

  {
    q: "What kind of notes are available on the platform?",
    a: "The platform provides Prelims-focused notes, Mains enrichment notes, current affairs value additions, diagrams, facts, and revision-oriented content."
  },

  {
    q: "How does the Daily MCQ Practice help?",
    a: "Daily MCQs are designed according to UPSC standards to improve conceptual clarity, elimination skills, and exam-level accuracy."
  },

  {
    q: "Does the platform support Mains answer writing practice?",
    a: "Yes. Aspirants can practice daily Mains questions, improve answer structure, and upload handwritten answers for practice."
  },

  {
    q: "Are Prelims and Mains both covered together?",
    a: "Yes. UPSC Journey follows an integrated preparation approach where Prelims and Mains preparation support each other instead of being treated separately."
  },

  {
  q: "Can I attempt test series multiple times?",
  a: "Yes. Users can attempt tests multiple times to improve accuracy, revision, and exam performance. Previous attempts and performance analysis remain available for review."
},

  {
    q: "Is the platform mobile-friendly?",
    a: "Yes. UPSC Journey is fully responsive and works smoothly across mobile devices, tablets, and desktops."
  },

  {
    q: "How frequently is content updated?",
    a: "Editorials, MCQs, notes, and preparation resources are updated regularly to stay aligned with current affairs and UPSC requirements."
  },

  {
    q: "How do I upgrade to Premium?",
    a: "You can create a free account first and upgrade to Premium anytime directly from the platform."
  },

  {
    q: "What payment methods are supported?",
    a: "We support UPI, debit cards, credit cards, and net banking for secure and seamless payments."
  },

  {
    q: "What should I do if Premium access is not activated after payment?",
    a: "Please contact our support team with your registered email and payment details. We will resolve the issue as quickly as possible."
  },

  {
    q: "Will new features and courses be added in future?",
    a: "Yes. UPSC Journey will continue expanding with more preparation tools, advanced analytics, and future learning programs."
  },

  {
    q: "How can I reset my password?",
    a: "Use the 'Forgot Password' option available on the login page to securely reset your password."
  },
];

// Home Component
const Home = () => {
  const navigate = useNavigate();
  const [darkMode, setDarkMode] = useState(() => {
  if (typeof window !== "undefined") {
    const stored = localStorage.getItem("theme");
    if (stored) return stored === "dark";
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches;
  }
  return false;
});

  const [openIndex, setOpenIndex] = useState(null);
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const toggleFAQ = (index) => setOpenIndex(openIndex === index ? null : index);

  useEffect(() => {
    const root = document.documentElement;
    if (darkMode) {
      root.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      root.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [darkMode]);

  useEffect(() => {
  const handleScroll = () => {
    setShowStickyCTA(window.scrollY > 400);
  };

  window.addEventListener("scroll", handleScroll);

  return () =>
    window.removeEventListener("scroll", handleScroll);
}, []);

  return (
    <div className="relative min-h-screen font-sans bg-gray-50 dark:bg-gray-900 transition-colors duration-700">
      <Helmet>
  <title>UPSC Journey – Complete UPSC Preparation Platform</title>

  <meta
    name="description"
    content="UPSC Journey is a complete UPSC preparation platform offering daily editorial analysis, subject-wise notes, current affairs, MCQs, answer writing practice, and test series for Prelims and Mains."
  />

  <meta name="robots" content="index, follow" />

  <link rel="canonical" href="https://upscjourney.com/" />

  {/* Open Graph (for Google & sharing) */}
  <meta property="og:title" content="UPSC Journey – Smart UPSC Preparation" />
  <meta
    property="og:description"
    content="Daily editorials, notes, MCQs, study plans, answer writing and test series for UPSC aspirants."
  />
  <meta property="og:url" content="https://upscjourney.com/" />
  <meta property="og:type" content="website" />

  {/* ✅ Organization Logo Structured Data */}
  <script type="application/ld+json">
    {`
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "UPSC Journey",
        "url": "https://upscjourney.com",
        "logo": "https://upscjourney.com/logo.png"
      }
    `}
  </script>
</Helmet>

      {/* Navbar */}
      <header className="fixed top-0 left-0 w-full bg-black bg-opacity-30 dark:bg-gray-900 dark:bg-opacity-40 backdrop-blur-sm z-50 flex items-center justify-between px-5 py-2 text-white shadow-md">
        <div className="flex items-center space-x-3">
          <img src="/logo.png" alt="UPSC Journey Logo" className="h-10 w-auto md:h-12 rounded-lg shadow-lg" />
          <motion.span
  className="text-xl md:text-2xl font-bold tracking-wider bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 to-red-400"
>
  UPSC Journey
</motion.span>
        </div>
        <div className="flex items-center space-x-4">
          <button onClick={() => navigate("/login")} className="px-4 py-1 bg-white/90 text-blue-900 rounded-md shadow hover:scale-[1.04]
hover:shadow-2xl
active:scale-95 hover:shadow-lg transition transform text-sm md:text-base">Login</button>
          <button onClick={() => navigate("/signup")} className="px-5 py-1 bg-gradient-to-r from-yellow-400 to-red-400 text-gray-900 font-semibold rounded-md shadow hover:scale-[1.04]
hover:shadow-2xl
active:scale-95 transition transform text-sm md:text-base">Sign Up</button>
          <button onClick={() => setDarkMode(!darkMode)} aria-label="Toggle Dark Mode" className="p-2 rounded-full hover:bg-gray-700/40 dark:hover:bg-gray-700 transition">
            {darkMode ? <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><circle cx={12} cy={12} r={5} stroke="currentColor" strokeWidth={2} /></svg>
              : <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12.79A9 9 0 1112.21 3a7 7 0 009.79 9.79z" /></svg>}
          </button>
        </div>
      </header>

  {/* Hero Section */}
<section className="w-full min-h-[90vh] grid grid-cols-1 md:grid-cols-2">
  
  {/* 🔥 LEFT SIDE (CONTENT - CLEAN BACKGROUND) */}
  <div className="flex items-center justify-center bg-black px-6 md:px-12 py-16">
    <motion.div
      variants={fadeInUp}
      initial="hidden"
      animate="visible"
      transition={{ duration: 1 }}
      className="max-w-lg text-left"
    >
      {/* HEADING */}
      <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-4 leading-tight">
        <span className="bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 to-red-400">
          Complete UPSC Preparation Platform
        </span>
        <br />
        <span className="text-white text-xl md:text-2xl font-semibold">
          for Prelims + Mains
        </span>
      </h1>

      {/* SUBHEADING */}
      <p className="text-sm sm:text-base md:text-lg text-white/90 font-medium mb-5">
        Daily Editorials • Notes • MCQs • Answer Writing • Test Series — All in One Place
      </p>

      {/* VALUE POINTS */}
      <div className="text-sm md:text-base text-white/80 space-y-2 mb-6">
        <p>✔ Structured Study Plan for Daily Consistency</p>
        <p>✔ UPSC-Level Practice with Detailed Explanations</p>
        <p>✔ Designed for Beginners & Working Aspirants</p>
      </div>

      {/* CTA */}
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={() => navigate("/signup")}
          className="px-6 py-2 bg-gradient-to-r from-yellow-400 to-red-400 text-gray-900 font-semibold rounded-md shadow hover:scale-[1.04]
hover:shadow-2xl
active:scale-95 transition"
        >
          Start Your Preparation
        </button>

        <button
  onClick={() => {
    document
      .getElementById("features")
      ?.scrollIntoView({ behavior: "smooth" });
  }}
  className="px-6 py-2 border border-white/70 text-white rounded-md hover:bg-white/10 transition"
>
  Explore Features
</button>
      </div>
    </motion.div>
  </div>

  {/* 🔥 RIGHT SIDE (IMAGE - FULLY VISIBLE) */}
  <div
    className="relative min-h-[300px] md:min-h-full bg-cover bg-center"
    style={{
      backgroundImage: 'url("/hero-bg.webp")',
    }}
  >
    {/* Optional subtle overlay for polish */}
    <div className="absolute inset-0 bg-black/20"></div>
  </div>

</section>

<div
className="
h-px
bg-gradient-to-r
from-transparent
via-yellow-400/20
to-transparent
"
/>

{/* 🔥 TRUST SECTION */}
<section className="py-24 md:py-28 px-6 bg-gradient-to-br from-gray-50 via-white to-yellow-50 dark:from-gray-900 dark:to-gray-800">
  
  <div className="max-w-[1400px] mx-auto text-center">
    
    {/* HEADING */}
    <h2 className="text-3xl md:text-4xl font-extrabold mb-4 text-gray-900 dark:text-yellow-400">
      Why Aspirants Trust UPSC Journey
    </h2>

    <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto mb-12 text-lg">
      Built with a clear focus on consistency, clarity, and real exam requirements — not just content overload.
    </p>

    {/* TRUST CARDS */}
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
      
      {/* CARD 1 */}
      <div className="p-6 rounded-2xl bg-white dark:bg-gray-800 shadow-lg hover:-translate-y-2
hover:shadow-2xl
transition-all
duration-300">
        <div className="text-4xl mb-4">📚</div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-yellow-300 mb-2">
          Structured Preparation
        </h3>
        <p className="text-gray-600 dark:text-gray-300 text-sm">
          Follow a clear, step-by-step study plan designed for UPSC Prelims & Mains.
        </p>
      </div>

      {/* CARD 2 */}
      <div className="p-6 rounded-2xl bg-white dark:bg-gray-800 shadow-lg hover:-translate-y-2
hover:shadow-2xl
transition-all
duration-300">
        <div className="text-4xl mb-4">🧠</div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-yellow-300 mb-2">
          Exam-Oriented Content
        </h3>
        <p className="text-gray-600 dark:text-gray-300 text-sm">
          Everything is aligned with UPSC pattern — no unnecessary information.
        </p>
      </div>

      {/* CARD 3 */}
      <div className="p-6 rounded-2xl bg-white dark:bg-gray-800 shadow-lg hover:-translate-y-2
hover:shadow-2xl
transition-all
duration-300">
        <div className="text-4xl mb-4">📅</div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-yellow-300 mb-2">
          Daily Consistency System
        </h3>
        <p className="text-gray-600 dark:text-gray-300 text-sm">
          Daily editorials, MCQs, and answer writing to keep you consistent.
        </p>
      </div>

    </div>

    {/* 🔥 STATS SECTION */}
    <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8">
      
      <div>
        <h3 className="text-3xl font-extrabold text-yellow-500">
<CountUp end={500} duration={2} />+
</h3>
        <p className="text-gray-600 dark:text-gray-300 text-sm">Aspirants Joined</p>
      </div>

      <div>
        <h3 className="text-3xl font-extrabold text-yellow-500"><CountUp end={10000} duration={2.5} separator="," />+</h3>
        <p className="text-gray-600 dark:text-gray-300 text-sm">MCQs Practiced</p>
      </div>

      <div>
        <h3 className="text-3xl font-extrabold text-yellow-500"><CountUp end={1000} duration={2} />+</h3>
        <p className="text-gray-600 dark:text-gray-300 text-sm">Answers Written</p>
      </div>

      <div>
        <h3 className="text-3xl font-extrabold text-yellow-500">Daily</h3>
        <p className="text-gray-600 dark:text-gray-300 text-sm">Content Updates</p>
      </div>

    </div>

  </div>
</section>

<div
className="
h-px
bg-gradient-to-r
from-transparent
via-yellow-400/20
to-transparent
"
/>

{/* 🔥 WHY CHOOSE US */}
<section className="py-24 md:py-28 px-6 bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
  
  <div className="max-w-[1400px] mx-auto text-center">
    
    {/* HEADING */}
    <h2 className="text-3xl md:text-4xl font-extrabold mb-4 text-gray-900 dark:text-yellow-400">
      Why Choose UPSC Journey?
    </h2>

    <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto mb-14 text-lg">
      Not just content — a complete system designed to make your preparation focused, structured, and result-oriented.
    </p>

    {/* GRID */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">

      {/* CARD 1 */}
      <div className="p-6 rounded-2xl bg-white dark:bg-gray-800 shadow-md hover:-translate-y-2
hover:shadow-2xl
transition-all
duration-300">
        <div className="text-4xl mb-4">🎯</div>
        <h3 className="text-lg font-bold text-gray-900 dark:text-yellow-300 mb-2">
          Integrated Preparation
        </h3>
        <p className="text-gray-600 dark:text-gray-300 text-sm">
          Prelims + Mains prepared together — not separately like most platforms.
        </p>
      </div>

      {/* CARD 2 */}
      <div className="p-6 rounded-2xl bg-white dark:bg-gray-800 shadow-md hover:-translate-y-2
hover:shadow-2xl
transition-all
duration-300">
        <div className="text-4xl mb-4">🧭</div>
        <h3 className="text-lg font-bold text-gray-900 dark:text-yellow-300 mb-2">
          No Random Study
        </h3>
        <p className="text-gray-600 dark:text-gray-300 text-sm">
          Follow a clear daily system instead of jumping between multiple sources.
        </p>
      </div>

      {/* CARD 3 */}
      <div className="p-6 rounded-2xl bg-white dark:bg-gray-800 shadow-md hover:-translate-y-2
hover:shadow-2xl
transition-all
duration-300">
        <div className="text-4xl mb-4">⚡</div>
        <h3 className="text-lg font-bold text-gray-900 dark:text-yellow-300 mb-2">
          Exam-Focused Approach
        </h3>
        <p className="text-gray-600 dark:text-gray-300 text-sm">
          Everything is designed strictly based on UPSC pattern & PYQs.
        </p>
      </div>

      {/* CARD 4 */}
      <div className="p-6 rounded-2xl bg-white dark:bg-gray-800 shadow-md hover:-translate-y-2
hover:shadow-2xl
transition-all
duration-300">
        <div className="text-4xl mb-4">📈</div>
        <h3 className="text-lg font-bold text-gray-900 dark:text-yellow-300 mb-2">
          Consistency System
        </h3>
        <p className="text-gray-600 dark:text-gray-300 text-sm">
          Daily tasks, practice & revision cycle to ensure steady progress.
        </p>
      </div>

    </div>

    {/* 🔥 COMPARISON LINE (POWERFUL) */}
    <div className="mt-16 max-w-3xl mx-auto">
      <p className="text-lg md:text-xl font-semibold text-gray-800 dark:text-gray-200">
        Most platforms give you <span className="text-red-500 font-bold">content</span>.
      </p>
      <p className="text-xl md:text-2xl font-extrabold mt-2 bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 to-red-400">
        We give you a complete preparation system.
      </p>
    </div>

  </div>
</section>

<div
className="
h-px
bg-gradient-to-r
from-transparent
via-yellow-400/20
to-transparent
"
/>

      {/* 🔥 PLATFORM FEATURES / WHAT YOU GET */}
<section
  id="features"
  className="py-24 md:py-28 px-6 bg-gradient-to-br from-gray-50 via-white to-yellow-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-800"
>

  <div className="max-w-[1400px] mx-auto text-center">

    {/* TOP LABEL */}
    <p className="uppercase tracking-[0.35em] text-yellow-500 text-sm font-semibold mb-3">
      Platform Features
    </p>

    {/* HEADING */}
    <h3 className="text-3xl md:text-5xl font-extrabold mb-5 text-gray-900 dark:text-yellow-400 tracking-tight leading-tight">
      Your UPSC Preparation Ecosystem
    </h3>

    {/* SUBTEXT */}
    <p className="text-gray-600 dark:text-gray-300 max-w-3xl mx-auto mb-14 text-lg leading-relaxed">
      Daily learning, practice, revision, and testing — all connected through one
      structured preparation workflow designed for Prelims and Mains.
    </p>

    {/* 🔥 FEATURE GRID */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">

      {/* CARD 1 */}
      <div className="group rounded-3xl bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl border border-yellow-100 dark:border-yellow-500/10 p-7 shadow-lg hover:-translate-y-2
hover:shadow-[0_25px_60px_rgba(255,193,7,0.15)]
transition-all
duration-300">
        <div
className="
text-5xl
mb-5
transition
duration-300
group-hover:scale-110
"
>📰</div>

        <h4 className="text-2xl font-extrabold text-gray-900 dark:text-yellow-300 mb-3">
          Daily Editorial Analysis
        </h4>

        <p className="text-gray-600 dark:text-gray-300 mb-5 leading-relaxed">
          Understand important editorials with direct UPSC relevance and simplified analysis.
        </p>

        <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 text-left">
          <li>✔ GS linkage</li>
          <li>✔ Key value points</li>
          <li>✔ Revision PDFs</li>
        </ul>
      </div>

      {/* CARD 2 */}
      <div className="group rounded-3xl bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl border border-yellow-100 dark:border-yellow-500/10 p-7 shadow-lg hover:-translate-y-2
hover:shadow-[0_25px_60px_rgba(255,193,7,0.15)]
transition-all
duration-300">
        <div
className="
text-5xl
mb-5
transition
duration-300
group-hover:scale-110
"
>📚</div>

        <h4 className="text-2xl font-extrabold text-gray-900 dark:text-yellow-300 mb-3">
          Notes Library
        </h4>

        <p className="text-gray-600 dark:text-gray-300 mb-5 leading-relaxed">
          Crisp and structured notes designed for quick understanding and revision.
        </p>

        <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 text-left">
          <li>✔ Prelims + Mains notes</li>
          <li>✔ Topic-wise structure</li>
          <li>✔ Diagrams & value adds</li>
        </ul>
      </div>

      {/* CARD 3 */}
      <div className="group rounded-3xl bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl border border-yellow-100 dark:border-yellow-500/10 p-7 shadow-lg hover:-translate-y-2
hover:shadow-[0_25px_60px_rgba(255,193,7,0.15)]
transition-all
duration-300">
        <div
className="
text-5xl
mb-5
transition
duration-300
group-hover:scale-110
"
>❓</div>

        <h4 className="text-2xl font-extrabold text-gray-900 dark:text-yellow-300 mb-3">
          Daily MCQ Practice
        </h4>

        <p className="text-gray-600 dark:text-gray-300 mb-5 leading-relaxed">
          Build accuracy, elimination skill, and conceptual clarity with daily practice.
        </p>

        <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 text-left">
          <li>✔ UPSC-pattern MCQs</li>
          <li>✔ Detailed explanations</li>
          <li>✔ Performance improvement</li>
        </ul>
      </div>

      {/* CARD 4 */}
      <div className="group rounded-3xl bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl border border-yellow-100 dark:border-yellow-500/10 p-7 shadow-lg hover:-translate-y-2
hover:shadow-[0_25px_60px_rgba(255,193,7,0.15)]
transition-all
duration-300">
        <div
className="
text-5xl
mb-5
transition
duration-300
group-hover:scale-110
"
>🗓️</div>

        <h4 className="text-2xl font-extrabold text-gray-900 dark:text-yellow-300 mb-3">
          Structured Study Plans
        </h4>

        <p className="text-gray-600 dark:text-gray-300 mb-5 leading-relaxed">
          Follow a clear daily and weekly roadmap designed for consistent preparation.
        </p>

        <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 text-left">
          <li>✔ Daily targets</li>
          <li>✔ Weekly planning</li>
          <li>✔ Beginner-friendly system</li>
        </ul>
      </div>

      {/* CARD 5 */}
      <div className="group rounded-3xl bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl border border-yellow-100 dark:border-yellow-500/10 p-7 shadow-lg hover:-translate-y-2
hover:shadow-[0_25px_60px_rgba(255,193,7,0.15)]
transition-all
duration-300">
       <div
className="
text-5xl
mb-5
transition
duration-300
group-hover:scale-110
"
>✍️</div>

        <h4 className="text-2xl font-extrabold text-gray-900 dark:text-yellow-300 mb-3">
          Mains Answer Writing
        </h4>

        <p className="text-gray-600 dark:text-gray-300 mb-5 leading-relaxed">
          Improve answer structure, presentation, and analytical writing daily.
        </p>

        <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 text-left">
          <li>✔ Daily practice questions</li>
          <li>✔ Structured approach</li>
          <li>✔ Upload handwritten answers</li>
        </ul>
      </div>

      {/* CARD 6 */}
      <div className="group rounded-3xl bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl border border-yellow-100 dark:border-yellow-500/10 p-7 shadow-lg hover:-translate-y-2
hover:shadow-[0_25px_60px_rgba(255,193,7,0.15)]
transition-all
duration-300">
        <div
className="
text-5xl
mb-5
transition
duration-300
group-hover:scale-110
"
>📝</div>

        <h4 className="text-2xl font-extrabold text-gray-900 dark:text-yellow-300 mb-3">
          Test Series
        </h4>

        <p className="text-gray-600 dark:text-gray-300 mb-5 leading-relaxed">
          Evaluate your preparation with UPSC-level Prelims and Mains tests.
        </p>

        <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300 text-left">
          <li>✔ Full-length tests</li>
          <li>✔ Detailed solutions</li>
          <li>✔ Unlimited review access</li>
        </ul>
      </div>

    </div>

    {/* 🔥 WORKFLOW STRIP */}
    <div className="mt-16">

      <p className="text-lg md:text-xl text-gray-700 dark:text-gray-300 font-medium mb-4">
        One platform. One workflow. One focused preparation journey.
      </p>

      <div className="flex flex-wrap justify-center items-center gap-4 md:gap-7 text-lg md:text-2xl font-bold text-yellow-600 dark:text-yellow-400 mt-6">

  <span className="flex items-center gap-2">
    <span className="text-2xl md:text-3xl">📖</span>
    Learn
  </span>

  <span className="opacity-40 text-xl md:text-3xl">→</span>

  <span className="flex items-center gap-2">
    <span className="text-2xl md:text-3xl">🧠</span>
    Understand
  </span>

  <span className="opacity-40 text-xl md:text-3xl">→</span>

  <span className="flex items-center gap-2">
    <span className="text-2xl md:text-3xl">❓</span>
    Practice
  </span>

  <span className="opacity-40 text-xl md:text-3xl">→</span>

  <span className="flex items-center gap-2">
    <span className="text-2xl md:text-3xl">🔁</span>
    Revise
  </span>

  <span className="opacity-40 text-xl md:text-3xl">→</span>

  <span className="flex items-center gap-2">
    <span className="text-2xl md:text-3xl">📝</span>
    Test
  </span>

  <span className="opacity-40 text-xl md:text-3xl">→</span>

  <span className="flex items-center gap-2">
    <span className="text-2xl md:text-3xl">📈</span>
    Improve
  </span>

</div>

    </div>

  </div>
</section>

<div
className="
h-px
bg-gradient-to-r
from-transparent
via-yellow-400/20
to-transparent
"
/>

      {/* 🔥 PRICING SECTION */}
<section className="py-24 md:py-28 px-6 bg-gradient-to-br from-yellow-50 via-white to-yellow-100 dark:from-gray-900 dark:to-gray-800 text-center">
  
  <div className="max-w-6xl mx-auto">

    {/* HEADING */}
    <h2 className="text-3xl md:text-4xl font-extrabold mb-4 text-gray-900 dark:text-yellow-400">
      Start Free. Upgrade When You’re Ready.
    </h2>

    <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto mb-14 text-lg">
      Explore the platform for free, then unlock full preparation with Premium access.
    </p>

    {/* GRID */}
    <div className="grid md:grid-cols-3 gap-8 items-stretch">

      {/* 🟢 FREE PLAN */}
      <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-lg p-8 border hover:-translate-y-2
hover:shadow-2xl
transition-all
duration-300">
        
        <h3 className="text-2xl font-extrabold mb-4 text-gray-900 dark:text-yellow-300">
          Free Access
        </h3>

        <p className="text-gray-600 dark:text-gray-300 mb-6">
          Get started and explore the platform
        </p>

        <div className="mb-6">
          <span className="text-4xl font-extrabold text-green-500">₹0</span>
        </div>

        <ul className="text-left space-y-3 mb-8 text-gray-700 dark:text-gray-300">
          <li>✔ Daily MCQs (limited)</li>
          <li>✔ Recent Editorials</li>
          <li>✔ Sample Notes Access</li>
          <li>✔ Dashboard Preview</li>
        </ul>

        <button
          onClick={() => navigate("/signup")}
          className="w-full py-3 border border-green-500 text-green-500 font-semibold rounded-lg hover:bg-green-500 hover:text-white transition"
        >
          Sign Up Free
        </button>

        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
          No payment required
        </p>
      </div>

      {/* 🔥 PREMIUM PLAN */}
      <div className="
relative

md:scale-[1.04]

bg-white
dark:bg-gray-800

rounded-3xl

shadow-2xl

border-2
border-yellow-400

hover:scale-[1.06]

transition-all
duration-300
">

        {/* BADGE */}
        <div className="absolute -top-4 right-6 bg-gradient-to-r from-yellow-400 to-red-400 text-gray-900 px-4 py-1 rounded-full text-sm font-bold shadow">
          Most Popular
        </div>

        <h3 className="text-2xl md:text-3xl font-extrabold mb-4 text-gray-900 dark:text-yellow-300">
          Premium Access
        </h3>

        <p className="text-gray-600 dark:text-gray-300 mb-6">
          Complete preparation system for Prelims + Mains
        </p>

        {/* PRICE */}
        <div className="mb-6">
          <span className="text-4xl font-extrabold text-yellow-500">₹3,999</span>
          <span className="text-gray-500 dark:text-gray-400 text-sm"> / year</span>
          <p className="mt-2 text-sm text-green-600 dark:text-green-400 font-medium">
Less than ₹11/day for complete preparation
</p>
        </div>

        {/* FEATURES */}
        <ul className="text-left space-y-3 mb-8 text-gray-700 dark:text-gray-300">
          <li>✔ Full Notes (Prelims + Mains)</li>
          <li>✔ Complete MCQ Practice</li>
          <li>✔ Full Editorial Archive</li>
          <li>✔ Prelims Test Series</li>
          <li>✔ Mains Answer Writing</li>
          <li>✔ Performance Tracking</li>          
        </ul>
        <div className="flex flex-wrap gap-2 mt-6">

<span className="
px-3
py-1
rounded-full
bg-green-100
dark:bg-green-900/40
text-green-700
dark:text-green-300
text-xs
font-semibold
">
Secure Payment
</span>

<span className="
px-3
py-1
rounded-full
bg-blue-100
dark:bg-blue-900/40
text-blue-700
dark:text-blue-300
text-xs
font-semibold
">
Instant Access
</span>

</div>

        {/* CTA */}
        <button
          onClick={() => navigate("/signup")}
          className="w-full py-3 bg-gradient-to-r from-yellow-400 to-red-400 text-gray-900 font-semibold rounded-lg shadow hover:scale-[1.04]
hover:shadow-2xl
active:scale-95transition"
        >
          Unlock Full Preparation
        </button>

        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
          Start free → upgrade anytime
        </p>
      </div>

      {/* 🔵 STUDY PLAN ADD-ON */}
      <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-lg p-8 hover:-translate-y-2
hover:shadow-2xl
transition-all
duration-300">

        <h3 className="text-2xl md:text-3xl font-extrabold mb-4 text-gray-900 dark:text-yellow-300">
          Study Plan Add-On
        </h3>

        <p className="text-gray-600 dark:text-gray-300 mb-6">
          Structured daily & weekly plans for disciplined preparation
        </p>

        <div className="mb-6">
          <span className="text-3xl font-extrabold text-blue-500">
            Starts from ₹99
          </span>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Available for all users (free & premium)
          </p>
        </div>

        <ul className="text-left space-y-3 mb-8 text-gray-700 dark:text-gray-300">
          <li>✔ Daily & Weekly Structure</li>
          <li>✔ Prelims + Mains Planning</li>
          <li>✔ PDF-Based Guidance</li>
          <li>✔ Designed for consistency</li>
        </ul>

        <button
          onClick={() => navigate("/signup")}
          className="w-full py-3 border border-blue-500 text-blue-500 font-semibold rounded-lg hover:bg-blue-500 hover:text-white transition"
        >
          Explore Study Plans
        </button>

        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
          Works with any plan
        </p>
      </div>

    </div>

    {/* 🔥 TRUST LINE */}
    <div className="mt-16">
      <p className="text-lg text-gray-700 dark:text-gray-300 font-semibold">
        No confusion. No pressure.
      </p>
      <p className="text-xl md:text-2xl font-extrabold mt-2 bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 to-red-400">
        Start free → Upgrade when you see the value
      </p>
    </div>

  </div>
</section>

<div
className="
h-px
bg-gradient-to-r
from-transparent
via-yellow-400/20
to-transparent
"
/>

{/* FAQ Section */}
<section className="py-24 md:py-28 px-6 bg-gradient-to-br from-gray-50 via-yellow-50 to-yellow-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-700">
  <h3 className="text-4xl font-extrabold text-center mb-16 text-yellow-600 dark:text-yellow-400 tracking-tight">
    Frequently Asked Questions
  </h3>
  <div className="max-w-4xl mx-auto space-y-6">
    {faqData.map((faq, idx) => (
      <div
        key={idx}
        className="bg-gradient-to-r from-yellow-50 to-yellow-100 dark:from-gray-900 dark:to-gray-800 rounded-xl shadow-lg p-6 cursor-pointer border border-yellow-200 dark:border-yellow-600"
        onClick={() => toggleFAQ(idx)}
      >
        <div className="flex justify-between items-center">
          <h4 className="text-lg font-semibold text-gray-900 dark:text-yellow-300">
            {faq.q}
          </h4>
          <span className="text-yellow-500 font-bold text-xl">
            {openIndex === idx ? "-" : "+"}
          </span>
        </div>
        <AnimatePresence>
          {openIndex === idx && (
            <motion.p
             initial={{ opacity: 0, height: 0 }}
             animate={{ opacity: 1, height: "auto" }}
             exit={{ opacity: 0, height: 0 }}
             transition={{ duration: 0.4 }}
             className="mt-3 text-gray-700 dark:text-gray-300 leading-relaxed"
           >
             {faq.a}
           </motion.p>
          )}
        </AnimatePresence>
      </div>
    ))}
  </div>
</section>

<div
className="
h-px
bg-gradient-to-r
from-transparent
via-yellow-400/20
to-transparent
"
/>

{/* Contact Form Section */}
<section className="py-24 md:py-28 px-6 bg-gradient-to-br from-gray-50 via-yellow-50 to-yellow-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-700">
  <div className="max-w-5xl mx-auto text-center mb-14">
    <h3 className="text-4xl font-extrabold mb-6 text-yellow-600 dark:text-yellow-400 tracking-tight">
      Still Have Questions?
    </h3>
    <p className="text-lg text-gray-700 dark:text-gray-300 max-w-2xl mx-auto">
      Reach out to us directly. Our team will get back to you within 24 hours.
    </p>
  </div>

  <ContactForm />
</section>

      {/* Footer */}
<footer className="relative bg-gradient-to-r from-blue-900 to-indigo-900 dark:from-gray-950 dark:to-gray-900 text-white py-20 px-10 overflow-hidden rounded-t-3xl shadow-2xl">
  {/* Decorative glow */}
  <div className="absolute top-0 right-0 w-72 h-72 bg-yellow-400/30 rounded-full blur-3xl pointer-events-none transform translate-x-1/2 -translate-y-1/2"></div>

  <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-12">
    
    {/* Brand */}
    <div className="space-y-6">
      <h3 className="text-4xl font-extrabold tracking-wide text-yellow-400 drop-shadow-lg">
        UPSC Journey
      </h3>
      <p className="text-yellow-200 max-w-xs font-light leading-relaxed tracking-wide">
        Empowering UPSC aspirants with expert content, personalized guidance, and a thriving community.
      </p>
      <a
        href="/signup"
        className="inline-block rounded-full px-8 py-3 font-semibold bg-gradient-to-r from-yellow-400 to-red-400 text-blue-900 shadow-lg hover:scale-[1.04]
hover:shadow-2xl
active:scale-95 transition"
      >
        Join Now
      </a>
    </div>

    {/* Quick Links */}
    <div>
      <h4 className="text-xl font-semibold mb-6 border-b-4 border-yellow-400 inline-block pb-2 tracking-wide">
        Quick Links
      </h4>
      <ul className="space-y-4 text-yellow-200 text-lg font-medium">
        <li><a href="/" className="hover:text-yellow-400 hover:underline transition">Home</a></li>
        <li><a href="/signup" className="hover:text-yellow-400 hover:underline transition">Sign Up</a></li>
        <li><a href="/login" className="hover:text-yellow-400 hover:underline transition">Login</a></li>
        <li><a href="/#about" className="hover:text-yellow-400 hover:underline transition">About</a></li>
        <li><a href="/privacy-policy" className="hover:text-yellow-400 hover:underline transition">Privacy Policy</a></li>
        <li><a href="/terms" className="hover:text-yellow-400 hover:underline transition">Terms & Conditions</a></li>
        <li><a href="/disclaimer" className="hover:text-yellow-400 hover:underline transition">Disclaimer</a></li>
        <li><a href="/refund-policy" className="hover:text-yellow-400 hover:underline transition">Refund & Cancellation Policy</a></li>
      </ul>
    </div>

    {/* Social Links */}
<div className="mt-8">

<p className="text-yellow-300 text-sm mb-5 font-medium">
Connect with UPSC Journey
</p>

<div className="grid grid-cols-2 gap-3 max-w-sm">

{/* Telegram */}
<a
href="https://t.me/+Y3rudYfqeQ5mYmE1"
target="_blank"
rel="noopener noreferrer"
className="
flex items-center gap-3
px-4 py-3
rounded-xl
bg-white/5
border border-white/10
hover:bg-white/10
transition
hover:scale-[1.03]
"
>
<svg
className="w-6 h-6 text-blue-400"
fill="currentColor"
viewBox="0 0 24 24"
>
<path d="M21 3L3 11l7 2 2 7 1-7 7-1z"/>
</svg>

<span className="text-white font-medium">
Telegram
</span>
</a>

{/* YouTube */}
<a
href="https://youtube.com/@upscjourney_uj?si=HBkjwgisXPeY8pD2"
target="_blank"
rel="noopener noreferrer"
className="
flex items-center gap-3
px-4 py-3
rounded-xl
bg-white/5
border border-white/10
hover:bg-white/10
transition
hover:scale-[1.03]
"
>
<svg
className="w-6 h-6 text-red-500"
fill="currentColor"
viewBox="0 0 24 24"
>
<path d="M21.8 8s-.2-1.4-.8-2c-.7-.8-1.5-.8-1.9-.9C16.6 5 12 5 12 5s-4.6 0-7.1.1c-.4 0-1.2.1-1.9.9-.6.6-.8 2-.8 2S2 9.6 2 11.3v1.5C2 14.4 2.2 16 2.2 16s.2 1.4.8 2c.7.8 1.6.8 2 .9 1.5.1 7 .1 7 .1s4.6 0 7.1-.1c.4 0 1.2-.1 1.9-.9.6-.6.8-2 .8-2s.2-1.6.2-3.2v-1.5zM9.8 14.5v-5l4.8 2.5z"/>
</svg>

<span className="text-white font-medium">
YouTube
</span>
</a>

{/* Instagram */}
<a
href="https://www.instagram.com/upscjourney_uj/"
target="_blank"
rel="noopener noreferrer"
className="
flex items-center gap-3
px-4 py-3
rounded-xl
bg-white/5
border border-white/10
hover:bg-white/10
transition
hover:scale-[1.03]
"
>
<svg
className="w-6 h-6 text-pink-500"
fill="currentColor"
viewBox="0 0 24 24"
>
<path d="M7 2C4.8 2 3 3.8 3 6v12c0 2.2 1.8 4 4 4h10c2.2 0 4-1.8 4-4V6c0-2.2-1.8-4-4-4H7zm5 5a5 5 0 110 10 5 5 0 010-10zm6.5-.9a1.1 1.1 0 11-2.2 0 1.1 1.1 0 012.2 0zM12 9a3 3 0 100 6 3 3 0 000-6z"/>
</svg>

<span className="text-white font-medium">
Instagram
</span>
</a>

{/* Facebook */}
<a
href="https://www.facebook.com/profile.php?id=61584109588211"
target="_blank"
rel="noopener noreferrer"
className="
flex items-center gap-3
px-4 py-3
rounded-xl
bg-white/5
border border-white/10
hover:bg-white/10
transition
hover:scale-[1.03]
"
>
<svg
className="w-6 h-6 text-blue-500"
fill="currentColor"
viewBox="0 0 24 24"
>
<path d="M22 12a10 10 0 10-11.5 9.9v-7h-2.3v-2.9h2.3V9.4c0-2.3 1.4-3.6 3.5-3.6 1 0 2 .2 2 .2v2.2h-1.2c-1.2 0-1.5.7-1.5 1.5v1.8h2.6l-.4 2.9h-2.2v7A10 10 0 0022 12z"/>
</svg>

<span className="text-white font-medium">
Facebook
</span>
</a>

</div>

      {/* Bottom line */}
      <div className="mt-12 flex items-center space-x-3 text-yellow-400 font-semibold text-lg select-none">
        <span role="img" aria-label="love" className="text-red-500 animate-pulse">❤️</span>
        <span>Made with passion for UPSC aspirants</span>
      </div>
    </div>
  </div>

  {/* Bottom strip */}
  <div className="mt-20 border-t border-yellow-500/30 pt-8 text-center text-yellow-300 text-sm tracking-wide">
    © {new Date().getFullYear()} UPSC Journey. All rights reserved.
  </div>
</footer>

      {/* Floating Social Bar */}
<div className="fixed bottom-6 right-6 flex flex-col items-center space-y-4 z-50">

  {/* Telegram */}
  <a
    href="https://t.me/upscjourney_uj"
    target="_blank"
    rel="noopener noreferrer"
    className="bg-gradient-to-r from-yellow-400 to-red-400 dark:from-blue-800 dark:to-indigo-700 text-blue-900 dark:text-yellow-400 p-4 rounded-full shadow-2xl hover:scale-110 transition"
    aria-label="Join UPSC Journey Telegram"
  >
    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
      <path d="M21 3L3 11l7 2 2 7 1-7 7-1z" />
    </svg>
  </a>

  {/* YouTube */}
  <a
    href="https://youtube.com/@upscjourney_uj?si=HBkjwgisXPeY8pD2"
    target="_blank"
    rel="noopener noreferrer"
    className="bg-red-600 text-white p-4 rounded-full shadow-2xl hover:scale-110 transition"
    aria-label="UPSC Journey YouTube"
  >
    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
      <path d="M21.8 8s-.2-1.4-.8-2c-.7-.8-1.5-.8-1.9-.9C16.6 5 12 5 12 5h0s-4.6 0-7.1.1c-.4 0-1.2.1-1.9.9-.6.6-.8 2-.8 2S2 9.6 2 11.3v1.5C2 14.4 2.2 16 2.2 16s.2 1.4.8 2c.7.8 1.6.8 2 .9 1.5.1 7 .1 7 .1s4.6 0 7.1-.1c.4 0 1.2-.1 1.9-.9.6-.6.8-2 .8-2s.2-1.6.2-3.2v-1.5C22 9.6 21.8 8 21.8 8zM9.8 14.5v-5l4.8 2.5-4.8 2.5z"/>
    </svg>
  </a>

  {/* Instagram */}
  <a
    href="https://www.instagram.com/upscjourney_uj/"
    target="_blank"
    rel="noopener noreferrer"
    className="bg-pink-500 text-white p-4 rounded-full shadow-2xl hover:scale-110 transition"
    aria-label="UPSC Journey Instagram"
  >
    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
      <path d="M7 2C4.8 2 3 3.8 3 6v12c0 2.2 1.8 4 4 4h10c2.2 0 4-1.8 4-4V6c0-2.2-1.8-4-4-4H7zm5 5a5 5 0 110 10 5 5 0 010-10zm6.5-.9a1.1 1.1 0 11-2.2 0 1.1 1.1 0 012.2 0zM12 9a3 3 0 100 6 3 3 0 000-6z"/>
    </svg>
  </a>

  {/* Facebook */}
  <a
    href="https://www.facebook.com/profile.php?id=61584109588211"
    target="_blank"
    rel="noopener noreferrer"
    className="bg-blue-600 text-white p-4 rounded-full shadow-2xl hover:scale-110 transition"
    aria-label="UPSC Journey Facebook"
  >
    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
      <path d="M22 12a10 10 0 10-11.5 9.9v-7h-2.3v-2.9h2.3V9.4c0-2.3 1.4-3.6 3.5-3.6 1 0 2 .2 2 .2v2.2h-1.2c-1.2 0-1.5.7-1.5 1.5v1.8h2.6l-.4 2.9h-2.2v7A10 10 0 0022 12z"/>
    </svg>
  </a>

</div>      

{/* Sticky CTA */}
<AnimatePresence>
  {showStickyCTA && (
    <motion.div
      initial={{ opacity: 0, y: 80 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 80 }}
      className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[80]"
    >
      <button
        onClick={() => navigate("/signup")}
        className="
          px-7
          py-3
          rounded-full
          bg-gradient-to-r
          from-yellow-400
          to-red-400
          text-gray-900
          font-bold
          shadow-2xl
          hover:scale-[1.04]
hover:shadow-2xl
active:scale-95
          transition
        "
      >
        🚀 Start Free Today
      </button>
    </motion.div>
  )}
</AnimatePresence>

{/* Consent bar */}
<ConsentBar />
    </div>
  );
};

export default Home;